/**
 * Collection infinite scroll.
 *
 * On any page with #product-grid:
 *  - Hides Dawn's pagination
 *  - Listens to scroll; when the sentinel comes within 800px of the viewport,
 *    fetches the next page (constructed from window.location.href with
 *    ?page=<current+1>, preserving filter/sort params)
 *  - Appends new .grid__item elements
 *  - Stops when fetched response either has no grid items OR was redirected
 *    (fetched pagination data-page != requested page)
 *
 * Logs to console so it's easy to debug if the loader doesn't fire.
 */
(function () {
  var TAG = '[infinite-scroll]';
  var DEBUG = false; // flip to true for verbose logging in console

  function log() {
    if (!DEBUG) return;
    var args = ['%c' + TAG, 'color:#1A8DA1'].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }
  function warn() {
    var args = ['%c' + TAG, 'color:#E85D2F'].concat(Array.prototype.slice.call(arguments));
    console.warn.apply(console, args);
  }

  function init() {
    var grid = document.querySelector('#product-grid');
    if (!grid) {
      log('no #product-grid on page, skipping');
      return;
    }
    var pagination = document.querySelector('.pagination-wrapper');
    if (!pagination) {
      log('no .pagination-wrapper on page, single-page collection — skipping');
      return;
    }
    log('init', { grid: grid, pagination: pagination });
    new InfiniteScroll(grid, pagination);
  }

  function InfiniteScroll(grid, pagination) {
    this.grid = grid;
    this.pagination = pagination;
    this.loading = false;
    this.hasMore = true;

    // Current page comes from the URL (?page=N). Fall back to the
    // pagination wrapper's data-page if URL has no param.
    var urlPage = this.urlPage();
    var dataPage = parseInt(pagination.dataset.page, 10);
    this.currentPage = urlPage || dataPage || 1;
    log('current page resolved to', this.currentPage, { urlPage: urlPage, dataPage: dataPage });

    this.hidePagination();
    this.mountSentinel();
    this.attachScroll();
    // Kick once on init in case the sentinel is already in view (short pages).
    this.maybeLoad();
  }

  var proto = InfiniteScroll.prototype;

  proto.urlPage = function () {
    try {
      return parseInt(new URL(window.location.href).searchParams.get('page'), 10) || null;
    } catch (e) {
      return null;
    }
  };

  proto.buildNextUrl = function (nextPage) {
    var url = new URL(window.location.href);
    url.searchParams.set('page', nextPage);
    return url.toString();
  };

  proto.hidePagination = function () {
    this.pagination.classList.add('infinite-scroll-hidden');
  };

  proto.mountSentinel = function () {
    this.sentinel = document.createElement('div');
    this.sentinel.className = 'infinite-scroll-sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');
    this.pagination.parentNode.insertBefore(this.sentinel, this.pagination);
    log('sentinel mounted', this.sentinel);
  };

  proto.attachScroll = function () {
    var self = this;
    var ticking = false;
    this.scrollHandler = function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        self.maybeLoad();
      });
    };
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    window.addEventListener('resize', this.scrollHandler, { passive: true });
  };

  proto.maybeLoad = function () {
    if (this.loading || !this.hasMore || !this.sentinel) return;
    var rect = this.sentinel.getBoundingClientRect();
    var threshold = window.innerHeight + 800;
    if (rect.top < threshold) {
      log('sentinel in range', { top: rect.top, threshold: threshold });
      this.loadMore();
    }
  };

  proto.loadMore = function () {
    var self = this;
    var nextPage = this.currentPage + 1;
    var url = this.buildNextUrl(nextPage);
    this.loading = true;
    this.sentinel.classList.add('is-loading');
    log('fetching', url);

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('http ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fetchedPagination = doc.querySelector('.pagination-wrapper');
        var fetchedPage = fetchedPagination
          ? parseInt(fetchedPagination.dataset.page, 10)
          : null;
        log('response pagination page=', fetchedPage, '(expected', nextPage + ')');

        // Shopify redirects ?page=N to the last valid page when N is out of
        // range. If the response's pagination doesn't match what we asked for,
        // we've already loaded the last page.
        if (fetchedPage !== null && fetchedPage !== nextPage) {
          log('response redirected to a different page → no more pages');
          self.hasMore = false;
          return;
        }

        var newGrid = doc.querySelector('#product-grid');
        if (!newGrid) {
          warn('no #product-grid in response');
          self.hasMore = false;
          return;
        }
        var items = newGrid.querySelectorAll('.grid__item');
        log('found', items.length, 'new items');
        if (items.length === 0) {
          self.hasMore = false;
          return;
        }

        var fragment = document.createDocumentFragment();
        items.forEach(function (item) {
          fragment.appendChild(item);
        });
        self.grid.appendChild(fragment);
        self.currentPage = nextPage;

        // Was there a next link in the fetched pagination? Dawn's "next"
        // arrow has class pagination__item--prev (yes, misleading).
        var fetchedNext =
          fetchedPagination &&
          fetchedPagination.querySelector('a.pagination__item--prev');
        if (!fetchedNext) {
          log('no next link in response → reached end');
          self.hasMore = false;
        }
      })
      .catch(function (err) {
        warn('load failed', err);
        // Stop attempting further loads on error to avoid hammering.
        self.hasMore = false;
        // Restore pagination so user can navigate manually as a fallback.
        self.pagination.classList.remove('infinite-scroll-hidden');
      })
      .finally(function () {
        self.loading = false;
        if (self.sentinel) self.sentinel.classList.remove('is-loading');
        if (!self.hasMore) {
          if (self.scrollHandler) {
            window.removeEventListener('scroll', self.scrollHandler);
            window.removeEventListener('resize', self.scrollHandler);
          }
          if (self.sentinel) {
            self.sentinel.remove();
            self.sentinel = null;
          }
          log('done — no more pages');
        } else {
          // Sentinel may still be within range after appending; check again
          // on the next frame so we keep loading until it's truly out of view.
          requestAnimationFrame(function () {
            self.maybeLoad();
          });
        }
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
